import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { segmentService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { tenantService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Loader2, X } from "lucide-react";

const EMPTY = { name: "", description: "", color: "#0d9488", tenantId: "" };

export default function SegmentsPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await segmentService.list({ size: 200 });
      setRows(d.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...EMPTY, ...row }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      if (editing) {
        const { name, description, color } = form;
        await segmentService.update(editing.id, { name, description, color });
        toast.success("Segment updated");
      } else {
        await segmentService.create(form);
        toast.success("Segment created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete segment "${row.name}"? Members will be detached.`)) return;
    try { await segmentService.remove(row.id); toast.success("Segment deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const viewMembers = async (row) => {
    try { const d = await segmentService.members(row.id); setMembers({ segment: row, items: d.items || [] }); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("SEGMENT.CREATE");
  const canEdit = hasPermission("SEGMENT.EDIT");
  const canDelete = hasPermission("SEGMENT.DELETE");

  const columns = [
    { key: "name", header: "Segment", render: (r) => (
      <span className="font-medium text-foreground flex items-center gap-2">
        <span className="h-3 w-3 rounded-full" style={{ background: r.color || "#0d9488" }} />{r.name}
      </span>
    ) },
    { key: "description", header: "Description", render: (r) => <span className="text-muted-foreground">{r.description || "—"}</span> },
    { key: "type", header: "Type", render: (r) => <Badge color="slate">{r.type}</Badge> },
    { key: "memberCount", header: "Members", render: (r) => (
      <button onClick={() => viewMembers(r)} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`members-${r.id}`}>
        <Users className="h-3.5 w-3.5" />{r.memberCount ?? 0}
      </button>
    ) },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="segments-page">
      <PageHeader
        title="Customer Segments"
        subtitle="Group customers for targeting and intelligence"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="segment-create-btn">
            <Plus className="h-4 w-4" /> New Segment
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="segments-table" empty="No segments yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="segment-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Segment</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="segment-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </div>
              )}
              <div><label className="block text-sm font-medium mb-1">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="segment-name" /></div>
              <div><label className="block text-sm font-medium mb-1">Description</label><input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ciq-input" /></div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <input type="color" value={form.color || "#0d9488"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-16 rounded border border-border bg-background" data-testid="segment-color" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="segment-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {members && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setMembers(null)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="segment-members-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Members · {members.segment.name}</h3>
              <button onClick={() => setMembers(null)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            {members.items.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No customers in this segment</p>
            ) : (
              <div className="space-y-2">
                {members.items.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm border-b border-border pb-2">
                    <span className="font-medium text-foreground">{c.customerName}</span>
                    <span className="text-xs text-muted-foreground">{c.customerCode}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
